import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Dumbbell, Target, Lightbulb, Layers, Maximize2 } from 'lucide-react';
import { Badge } from '../ui/badge';

/**
 * Read-only modal that shows a canonical exercise's demo video,
 * muscle-group tags, equipment, difficulty, and form tips.
 *
 * If neither `video_url` nor `youtube_url` is set, shows a polite
 * "Demo coming soon" state — the modal stays useful for the form-tips
 * and muscle-group info even before media has been uploaded.
 */

const youtubeEmbed = (url) => {
  if (!url) return null;
  // Handles common youtube.com/watch?v=, youtu.be/, youtube.com/shorts/ formats
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

export const ExerciseDemoModal = ({ exercise, onClose }) => {
  const [view, setView] = useState('athlete'); // 'athlete' | 'anatomy'
  const videoRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const requestFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    // iOS Safari uses a proprietary method on the video element itself
    if (v.webkitEnterFullscreen) return v.webkitEnterFullscreen();
    const target = v.requestFullscreen ? v : v.parentElement;
    const fn = target.requestFullscreen || target.webkitRequestFullscreen || target.msRequestFullscreen;
    if (fn) fn.call(target);
  };

  if (!exercise) return null;

  const ytEmbed = youtubeEmbed(exercise.youtube_url);
  const hasVideo = !!(exercise.video_url || ytEmbed);
  const hasAnatomy = !!exercise.anatomy_url;
  const hasMedia = hasVideo || hasAnatomy;
  const showTabs = hasVideo && hasAnatomy;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 px-4 py-8 overflow-y-auto"
      onClick={onClose}
      data-testid="exercise-demo-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-card border-2 border-primary/40 rounded-2xl max-w-2xl w-full my-auto overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-5 border-b border-border">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="h-10 w-10 flex items-center justify-center bg-primary/20 text-primary rounded-lg shrink-0">
              <Dumbbell className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-heading text-lg sm:text-xl text-white uppercase tracking-wider truncate" data-testid="exercise-demo-name">
                {exercise.name}
              </h3>
              {exercise.description && (
                <p className="text-muted-foreground text-sm truncate">{exercise.description}</p>
              )}
            </div>
          </div>

          {/* Athlete / Anatomy tab switcher — only when both media types exist */}
          {showTabs && (
            <div className="flex bg-black/60 border border-border rounded-full p-0.5 shrink-0" data-testid="exercise-demo-tabs">
              <button
                type="button"
                onClick={() => setView('athlete')}
                className={`px-3 py-1 text-[11px] font-heading uppercase tracking-wider rounded-full transition-colors ${view === 'athlete' ? 'bg-primary text-black' : 'text-muted-foreground hover:text-white'}`}
                data-testid="exercise-demo-tab-athlete"
              >
                Athlete
              </button>
              <button
                type="button"
                onClick={() => setView('anatomy')}
                className={`px-3 py-1 text-[11px] font-heading uppercase tracking-wider rounded-full transition-colors ${view === 'anatomy' ? 'bg-primary text-black' : 'text-muted-foreground hover:text-white'}`}
                data-testid="exercise-demo-tab-anatomy"
              >
                Anatomy
              </button>
            </div>
          )}

          {view === 'athlete' && hasVideo && (
            <button
              type="button"
              onClick={requestFullscreen}
              className="text-muted-foreground hover:text-white shrink-0"
              aria-label="Maximize video"
              data-testid="exercise-demo-fullscreen"
            >
              <Maximize2 className="h-5 w-5" />
            </button>
          )}

          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-white shrink-0"
            aria-label="Close"
            data-testid="exercise-demo-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Media */}
        <div className="bg-black aspect-video flex items-center justify-center">
          {view === 'anatomy' && hasAnatomy ? (
            <img
              src={exercise.anatomy_url}
              alt={`${exercise.name} muscles worked`}
              className="w-full h-full object-contain bg-[#f4ecd7]"
              data-testid="exercise-demo-anatomy"
            />
          ) : ytEmbed ? (
            <iframe
              src={ytEmbed}
              title={exercise.name}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              data-testid="exercise-demo-youtube"
            />
          ) : exercise.video_url ? (
            <video
              ref={videoRef}
              src={exercise.video_url}
              poster={exercise.thumbnail_url || undefined}
              controls
              playsInline
              onDoubleClick={requestFullscreen}
              className="w-full h-full"
              data-testid="exercise-demo-video"
            />
          ) : hasAnatomy ? (
            <img
              src={exercise.anatomy_url}
              alt={`${exercise.name} muscles worked`}
              className="w-full h-full object-contain bg-[#f4ecd7]"
              data-testid="exercise-demo-anatomy"
            />
          ) : exercise.thumbnail_url ? (
            <img src={exercise.thumbnail_url} alt={exercise.name} className="max-h-full max-w-full" />
          ) : (
            <div className="text-center text-muted-foreground py-12 px-6">
              <Dumbbell className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Demo video coming soon — admins are uploading these in batches.</p>
            </div>
          )}
        </div>

        {/* Meta + tips */}
        <div className="p-5 space-y-4">
          {/* Tags — muscle group badges are clickable: each one navigates to
              the public library filtered to that muscle so users can discover
              related lifts in one tap. */}
          <div className="flex flex-wrap gap-2" data-testid="exercise-demo-tags">
            {(exercise.muscle_groups || []).map((m) => (
              <Link
                key={m}
                to={`/exercises?muscle=${encodeURIComponent(m)}`}
                onClick={onClose}
                className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-primary/20 text-primary border-0 capitalize hover:bg-primary hover:text-black transition-colors"
                data-testid={`exercise-demo-muscle-link-${m.replace(/\s/g, '-')}`}
              >
                <Target className="h-3 w-3 mr-1" /> {m}
              </Link>
            ))}
            {exercise.equipment && (
              <Badge className="bg-blue-500/20 text-blue-300 border-0 capitalize">
                <Layers className="h-3 w-3 mr-1" /> {exercise.equipment}
              </Badge>
            )}
            {exercise.difficulty && (
              <Badge className="bg-orange-500/20 text-orange-300 border-0 capitalize">
                {exercise.difficulty}
              </Badge>
            )}
          </div>

          {/* Form tips */}
          {(exercise.form_tips || []).length > 0 && (
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider flex items-center gap-2 mb-2">
                <Lightbulb className="h-4 w-4 text-primary" /> Form Tips
              </h4>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {exercise.form_tips.map((tip) => (
                  <li key={tip} className="flex gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!hasMedia && !(exercise.form_tips || []).length && (
            <p className="text-sm text-muted-foreground text-center py-2">
              Tap the name in any workout to revisit this card.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
